import { useCallback, useMemo, useState } from 'react';
import { ChevronDown, ChevronUp, Pencil, Trash2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';

function newStepId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

// ── Calendar-day basis ────────────────────────────────────────────────────
// All date math is performed on the *calendar day* derived from a YYYY-MM-DD
// string, interpreted in UTC. ``today`` is the local calendar date formatted
// as YYYY-MM-DD, then fed through the same parser so markers and bars share
// one observable rule (no timezone drift between axis tick and today line).

/** Parse YYYY-MM-DD -> integer UTC-midnight day number. */
function toDayNumber(iso) {
  if (!iso || typeof iso !== 'string') return NaN;
  const [y, m, d] = iso.split('-').map((x) => parseInt(x, 10));
  if (!y || !m || !d) return NaN;
  return Math.floor(Date.UTC(y, m - 1, d) / 86400000);
}

/** Day number -> YYYY-MM-DD (UTC). */
function fromDayNumber(dayNum) {
  if (!Number.isFinite(dayNum)) return '';
  const u = new Date(dayNum * 86400000);
  const y = u.getUTCFullYear();
  const m = String(u.getUTCMonth() + 1).padStart(2, '0');
  const d = String(u.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/** User's local *calendar date* as YYYY-MM-DD (what <input type="date"> produces). */
function todayISO() {
  const n = new Date();
  const y = n.getFullYear();
  const mo = String(n.getMonth() + 1).padStart(2, '0');
  const da = String(n.getDate()).padStart(2, '0');
  return `${y}-${mo}-${da}`;
}

function addDaysISO(iso, deltaDays) {
  const base = toDayNumber(iso);
  if (!Number.isFinite(base)) return '';
  return fromDayNumber(base + deltaDays);
}

/** Inclusive end from start + duration (N calendar days). Display helper only. */
function endFromDuration(startIso, durationDays) {
  const n = parseInt(String(durationDays), 10);
  if (!Number.isFinite(n) || n < 1) return '';
  return addDaysISO(startIso, n - 1);
}

function effectiveEndIso(step) {
  if (step.end_date) return step.end_date;
  return endFromDuration(step.start_date, step.duration_days);
}

/**
 * Transport-only shaping of a form row driven by an explicit mode.
 *
 * Structural rules (start required, end ≥ start, duration ≥ 1) live in the
 * backend (`_parse_calendar_steps_list` in `backend/server.py`). The UI's
 * contribution is to remove the ambiguity of the two-field form by sending
 * only the field that matches the currently selected mode — the other field
 * is nulled out on the wire even if it happens to still hold a leftover
 * value in local state.
 */
function buildStepPayload(raw, mode, fallbackId) {
  const id = raw.id || fallbackId;
  const title = String(raw.title || '').trim();
  const notes = String(raw.notes || '').trim();
  const start_date = String(raw.start_date || '').trim();
  const endRaw = String(raw.end_date || '').trim();
  const durStr = String(raw.duration_days ?? '').trim();
  const durParsed = durStr === '' ? null : parseInt(durStr, 10);
  if (!start_date) {
    return { error: 'Start date is required.' };
  }
  if (mode === 'end') {
    if (!endRaw) return { error: 'Pick an end date or switch to Duration.' };
    return {
      step: {
        id,
        title: title || 'Untitled step',
        notes,
        start_date,
        end_date: endRaw,
        duration_days: null,
      },
    };
  }
  if (!Number.isFinite(durParsed) || durParsed < 1) {
    return { error: 'Duration must be at least 1 day.' };
  }
  return {
    step: {
      id,
      title: title || 'Untitled step',
      notes,
      start_date,
      end_date: null,
      duration_days: durParsed,
    },
  };
}

function formatShortDate(iso) {
  if (!iso) return '';
  const [y, m, d] = iso.split('-').map((x) => parseInt(x, 10));
  if (!y) return iso;
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

const emptyForm = () => ({
  title: '',
  notes: '',
  start_date: '',
  end_date: '',
  duration_days: '',
});

export function DealCalendarTab({ deal, onUpdateCalendarSteps, loading }) {
  const steps = useMemo(() => (Array.isArray(deal?.calendar_steps) ? deal.calendar_steps : []), [deal?.calendar_steps]);
  const [form, setForm] = useState(emptyForm);
  const [mode, setMode] = useState('end'); // 'end' | 'duration'
  const [editingId, setEditingId] = useState(null);
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const today = todayISO();

  const chartModel = useMemo(() => {
    if (!steps.length) {
      const t0 = toDayNumber(addDaysISO(today, -14));
      const t1 = toDayNumber(addDaysISO(today, 42));
      return { t0, t1, span: Math.max(1, t1 - t0 + 1), rows: [] };
    }
    let minD = Infinity;
    let maxD = -Infinity;
    const rows = steps.map((s) => {
      const start = s.start_date;
      const end = effectiveEndIso(s);
      const ds = toDayNumber(start);
      const de = toDayNumber(end);
      if (Number.isFinite(ds)) {
        minD = Math.min(minD, ds);
        maxD = Math.max(maxD, Number.isFinite(de) ? de : ds);
      }
      return { step: s, start, end, ds, de: Number.isFinite(de) ? de : ds };
    });
    const tToday = toDayNumber(today);
    if (!Number.isFinite(minD)) {
      minD = tToday - 7;
      maxD = tToday + 30;
    }
    minD = Math.min(minD, tToday) - 3;
    maxD = Math.max(maxD, tToday) + 3;
    const span = Math.max(1, maxD - minD + 1);
    return { t0: minD, t1: maxD, span, rows };
  }, [steps, today]);

  const persistSteps = useCallback(
    async (next) => {
      await onUpdateCalendarSteps?.(next);
    },
    [onUpdateCalendarSteps],
  );

  const handleAddOrUpdate = useCallback(async () => {
    if (submitting) return;
    setFormError('');
    const { step, error } = buildStepPayload(form, mode, editingId || newStepId());
    if (error) {
      setFormError(error);
      return;
    }
    let next;
    if (editingId) {
      next = steps.map((s) => (s.id === editingId ? step : s));
    } else {
      next = [...steps, step];
    }
    setSubmitting(true);
    try {
      await persistSteps(next);
      setForm(emptyForm());
      setEditingId(null);
      setMode('end');
    } catch (err) {
      // The caller (App.updateDeal) already surfaced the backend detail via
      // toast; preserve the form state so the user can correct and retry.
      const detail = err?.response?.data?.detail;
      if (typeof detail === 'string' && detail) setFormError(detail);
    } finally {
      setSubmitting(false);
    }
  }, [editingId, form, mode, persistSteps, steps, submitting]);

  const startEdit = useCallback((s) => {
    setEditingId(s.id);
    setFormError('');
    // Pick the mode that matches how the step was originally saved (end-date
    // mode wins if both happen to be non-null, mirroring backend precedence).
    const nextMode = s.end_date ? 'end' : s.duration_days != null ? 'duration' : 'end';
    setMode(nextMode);
    setForm({
      title: s.title || '',
      notes: s.notes || '',
      start_date: s.start_date || '',
      end_date: s.end_date || '',
      duration_days: s.duration_days != null ? String(s.duration_days) : '',
    });
  }, []);

  const cancelEdit = useCallback(() => {
    setEditingId(null);
    setForm(emptyForm());
    setFormError('');
    setMode('end');
  }, []);

  const switchMode = useCallback((nextMode) => {
    if (nextMode !== 'end' && nextMode !== 'duration') return;
    setMode(nextMode);
    setFormError('');
    // Clear the *other* field so stale values from the other mode can never
    // sneak into the wire payload or confuse the preview.
    setForm((f) => (nextMode === 'end' ? { ...f, duration_days: '' } : { ...f, end_date: '' }));
  }, []);

  const computedEndPreview = useMemo(() => {
    if (mode !== 'duration') return '';
    if (!form.start_date) return '';
    const n = parseInt(String(form.duration_days), 10);
    if (!Number.isFinite(n) || n < 1) return '';
    const iso = endFromDuration(form.start_date, n);
    return iso ? formatShortDate(iso) : '';
  }, [form.duration_days, form.start_date, mode]);

  const removeStep = useCallback(
    async (id) => {
      const next = steps.filter((s) => s.id !== id);
      try {
        await persistSteps(next);
        if (editingId === id) cancelEdit();
      } catch {
        // toast already surfaced by updateDeal
      }
    },
    [cancelEdit, editingId, persistSteps, steps],
  );

  const moveStep = useCallback(
    async (id, dir) => {
      const idx = steps.findIndex((s) => s.id === id);
      if (idx < 0) return;
      const j = dir === 'up' ? idx - 1 : idx + 1;
      if (j < 0 || j >= steps.length) return;
      const next = [...steps];
      [next[idx], next[j]] = [next[j], next[idx]];
      try {
        await persistSteps(next);
      } catch {
        // toast already surfaced
      }
    },
    [persistSteps, steps],
  );

  const pctForDay = useCallback(
    (dayNum) => {
      if (!Number.isFinite(dayNum)) return 0;
      const { t0, span } = chartModel;
      return ((dayNum - t0) / span) * 100;
    },
    [chartModel],
  );

  const todayPct = pctForDay(toDayNumber(today));

  const projectTitle = String(deal?.project_name || 'Project').trim() || 'Project';
  const todayLabel = formatShortDate(today);

  return (
    <div className="min-h-0 flex-1 overflow-y-auto bg-[#f5f6f8] p-4 md:p-8">
      <div className="mx-auto grid w-full max-w-[1180px] grid-cols-1 items-start gap-6 lg:grid-cols-[minmax(0,260px)_minmax(0,1fr)]">
        <section className="flex flex-col gap-4 rounded-xl border border-slate-200/80 bg-white p-4 shadow-sm lg:sticky lg:top-4">
            <h3 className="text-sm font-semibold text-slate-900">{editingId ? 'Edit step' : 'Add step'}</h3>
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="cal-step-title" className="text-xs text-slate-600">
                  Step
                </Label>
                <Input
                  id="cal-step-title"
                  value={form.title}
                  onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                  placeholder="e.g. Kickoff meeting"
                  className="h-9 text-sm"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="cal-step-notes" className="text-xs text-slate-600">
                  Notes
                </Label>
                <Textarea
                  id="cal-step-notes"
                  value={form.notes}
                  onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
                  placeholder="Optional context"
                  className="min-h-[72px] resize-y text-sm"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="cal-step-start" className="text-xs text-slate-600">
                  Start date
                </Label>
                <Input
                  id="cal-step-start"
                  type="date"
                  value={form.start_date}
                  onChange={(e) => setForm((f) => ({ ...f, start_date: e.target.value }))}
                  className="h-9 text-sm"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-slate-600">End by</Label>
                <div
                  role="tablist"
                  aria-label="End mode"
                  className="inline-flex w-full rounded-md border border-slate-200 bg-slate-50 p-0.5 text-xs"
                >
                  <button
                    type="button"
                    role="tab"
                    aria-selected={mode === 'end'}
                    onClick={() => switchMode('end')}
                    className={
                      'flex-1 rounded px-2 py-1 font-medium transition ' +
                      (mode === 'end'
                        ? 'bg-white text-slate-900 shadow-sm ring-1 ring-slate-200'
                        : 'text-slate-500 hover:text-slate-700')
                    }
                  >
                    End date
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={mode === 'duration'}
                    onClick={() => switchMode('duration')}
                    className={
                      'flex-1 rounded px-2 py-1 font-medium transition ' +
                      (mode === 'duration'
                        ? 'bg-white text-slate-900 shadow-sm ring-1 ring-slate-200'
                        : 'text-slate-500 hover:text-slate-700')
                    }
                  >
                    Duration
                  </button>
                </div>
              </div>
              {mode === 'end' ? (
                <div className="space-y-1.5">
                  <Label htmlFor="cal-step-end" className="text-xs text-slate-600">
                    End date
                  </Label>
                  <Input
                    id="cal-step-end"
                    type="date"
                    value={form.end_date}
                    onChange={(e) => setForm((f) => ({ ...f, end_date: e.target.value }))}
                    className="h-9 text-sm"
                  />
                </div>
              ) : (
                <div className="space-y-1.5">
                  <Label htmlFor="cal-step-dur" className="text-xs text-slate-600">
                    Duration (days)
                  </Label>
                  <Input
                    id="cal-step-dur"
                    type="number"
                    min={1}
                    step={1}
                    inputMode="numeric"
                    value={form.duration_days}
                    onChange={(e) => setForm((f) => ({ ...f, duration_days: e.target.value }))}
                    placeholder="e.g. 10"
                    className="h-9 text-sm"
                  />
                  {computedEndPreview ? (
                    <p className="text-[11px] text-slate-500">
                      Ends on <span className="font-medium text-slate-700">{computedEndPreview}</span>
                    </p>
                  ) : null}
                </div>
              )}
              {formError ? <p className="text-xs text-rose-600">{formError}</p> : null}
              <p className="text-[11px] leading-relaxed text-slate-500">
                Switch between modes at any time — the other field is cleared automatically.
              </p>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  size="sm"
                  className="h-8"
                  onClick={handleAddOrUpdate}
                  disabled={loading || submitting}
                >
                  {editingId ? 'Save step' : 'Add step'}
                </Button>
                {editingId ? (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-8"
                    onClick={cancelEdit}
                    disabled={loading || submitting}
                  >
                    Cancel
                  </Button>
                ) : null}
              </div>
            </div>

            {steps.length > 0 ? (
              <div className="mt-2 border-t border-slate-100 pt-3">
                <p className="mb-2 text-xs font-medium text-slate-600">Steps</p>
                <ul className="max-h-[220px] space-y-1.5 overflow-y-auto pr-1">
                  {steps.map((s, i) => (
                    <li
                      key={s.id}
                      className="flex items-start gap-2 rounded-lg border border-slate-100 bg-slate-50/60 px-2 py-2 text-xs"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="truncate font-medium text-slate-800">{s.title || 'Untitled step'}</p>
                        <p className="mt-0.5 text-[11px] text-slate-500">
                          {formatShortDate(s.start_date)}
                          {' → '}
                          {formatShortDate(effectiveEndIso(s))}
                          {s.duration_days != null && !s.end_date ? ` · ${s.duration_days}d` : null}
                        </p>
                      </div>
                      <div className="flex shrink-0 flex-col gap-0.5">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          disabled={loading || i === 0}
                          onClick={() => moveStep(s.id, 'up')}
                          title="Move up"
                        >
                          <ChevronUp className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7"
                          disabled={loading || i === steps.length - 1}
                          onClick={() => moveStep(s.id, 'down')}
                          title="Move down"
                        >
                          <ChevronDown className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 shrink-0 text-slate-500"
                        onClick={() => startEdit(s)}
                        title="Edit"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 shrink-0 text-slate-500 hover:text-rose-600"
                        onClick={() => removeStep(s.id)}
                        title="Delete"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </section>

          <section className="flex min-h-[480px] flex-col gap-5 rounded-2xl border border-slate-200/80 bg-white p-6 shadow-sm md:p-8">
            <header className="flex flex-col gap-1">
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Project timeline</p>
              <h2 className="text-xl font-semibold tracking-tight text-slate-900 md:text-2xl">{projectTitle}</h2>
              <p className="text-sm text-slate-500">Current planning status — shareable overview of key phases.</p>
            </header>

            <div className="flex items-center justify-between gap-2 border-t border-slate-100 pt-4">
              <h3 className="text-sm font-semibold text-slate-900">Timeline</h3>
              <span className="rounded-full border border-sky-200 bg-sky-50 px-2.5 py-0.5 text-[11px] font-medium text-sky-800">
                Today · {todayLabel}
              </span>
            </div>

            {steps.length === 0 ? (
              <div className="flex flex-1 flex-col items-center justify-center rounded-xl border border-dashed border-slate-200 bg-slate-50/40 p-10 text-center">
                <p className="text-sm font-medium text-slate-700">No steps yet.</p>
                <p className="mt-1 max-w-sm text-xs text-slate-500">Add phases on the left to build a presentation-ready Gantt view.</p>
              </div>
            ) : (
              <div className="min-h-0 flex-1 overflow-x-auto">
                <div className="min-w-[520px] rounded-lg border border-slate-200 bg-white">
                  <div className="grid grid-cols-[168px_1fr] border-b border-slate-100 bg-slate-50/80">
                    <div className="border-r border-slate-100 bg-slate-50/50" />
                    <div className="relative h-8">
                      <div
                        className="pointer-events-none absolute inset-y-1 z-10 w-px bg-sky-500 shadow-[0_0_0_1px_rgba(14,165,233,0.2)]"
                        style={{ left: `${todayPct}%`, transform: 'translateX(-50%)' }}
                      />
                      <div className="absolute inset-x-2 bottom-1 top-1 flex items-end justify-between text-[10px] text-slate-400">
                        {(() => {
                          const { t0, t1 } = chartModel;
                          const spanDays = t1 - t0 + 1;
                          const step = Math.max(1, Math.ceil(spanDays / 6));
                          const ticks = [];
                          for (let d = t0; d <= t1; d += step) {
                            ticks.push(d);
                          }
                          if (ticks[ticks.length - 1] !== t1) ticks.push(t1);
                          return ticks.map((d) => (
                            <span key={d} className="whitespace-nowrap">
                              {formatShortDate(fromDayNumber(d))}
                            </span>
                          ));
                        })()}
                      </div>
                    </div>
                  </div>

                  {chartModel.rows.map(({ step: s, ds, de }, rowIdx) => {
                    const endDay = Number.isFinite(de) ? de : ds;
                    const widthPct = Math.max(0.6, pctForDay(endDay) - pctForDay(ds));
                    const leftPct = pctForDay(ds);
                    const tNum = toDayNumber(today);
                    const overdue = Number.isFinite(endDay) && tNum > endDay;
                    const active = tNum >= ds && tNum <= endDay;
                    const barClass = overdue
                      ? 'border border-rose-200 bg-rose-100/90'
                      : active
                        ? 'border border-sky-300 bg-sky-100/90'
                        : 'border border-slate-200/90 bg-slate-200/70';
                    const isLast = rowIdx === chartModel.rows.length - 1;
                    const b = isLast ? '' : 'border-b border-slate-100';

                    return (
                      <div key={s.id} className={`grid grid-cols-[168px_1fr] ${b}`}>
                        <div className={`flex flex-col justify-center bg-slate-50/40 px-2 py-2 ${b ? 'border-r border-slate-100' : ''}`}>
                          <span className="truncate text-xs font-medium text-slate-800">{s.title || 'Untitled'}</span>
                          {s.notes ? <span className="mt-0.5 line-clamp-2 text-[10px] leading-snug text-slate-500">{s.notes}</span> : null}
                        </div>
                        <div className={`relative py-2 pr-2 ${b}`}>
                          <div
                            className="pointer-events-none absolute inset-y-2 z-10 w-px bg-sky-500/90"
                            style={{ left: `${todayPct}%`, transform: 'translateX(-50%)' }}
                          />
                          <div className="relative mx-1 h-7 rounded-sm bg-slate-50/90">
                            <div
                              className={`absolute top-0.5 h-6 rounded-sm ${barClass}`}
                              style={{
                                left: `${leftPct}%`,
                                width: `${widthPct}%`,
                                minWidth: '6px',
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </section>
      </div>
    </div>
  );
}
